import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import {
  approveFinding,
  rejectFinding,
  unreviewFinding,
} from '@/api/findings';
import type {
  InvoiceExtractionOut,
  InvoiceLinkOut,
} from '@/api/invoices';
import type { FindingOut, ReasonCode, RuleOut } from '@/api/types';
import { AttachmentChip } from '@/components/AttachmentChip';
import { CarriedForwardPill } from '@/components/CarriedForwardPill';
import { FindingFilterBar } from '@/components/FindingFilterBar';
import { FindingTags } from '@/components/FindingTags';
import { NoInvoiceBadge } from '@/components/NoInvoiceBadge';
import { ReasonCodesPicker } from '@/components/ReasonCodesPicker';
import { RecoveryAmountInline } from '@/components/RecoveryAmountInline';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusBadge as FindingStatusBadge } from '@/components/ui/status-badge';
import { useReturnToRow } from '@/hooks/useReturnToRow';
import { useWindowedRows } from '@/hooks/useWindowedRows';
import { type StatusFilter } from '@/hooks/useFindingsReview';
import {
  lastVisitedFindingKey,
  writeLastVisitedRow,
} from '@/lib/findingFilterStorage';
import { bulkApprovalReason } from '@/lib/approval-reasons';
import { findingMatchesSearch } from '@/lib/findingSearch';
import { formatDateTime } from '@/lib/format';
import { submitFormOnCmdEnter } from '@/lib/formKeys';
import { stopRowActivation } from '@/lib/rowLink';
import { useAuth } from '../auth/AuthContext';

/** Compact dispositioned-findings table. Per-row Approve / Reject
 *  re-using the same mutation shape as `FindingCard` — practitioner
 *  can correct an accidental approve / reject without leaving the
 *  audit view. Filter bar mirrors the queue's. */
export function AuditTable({
  findings,
  rules,
  canReview,
  runId,
  linksByTransaction,
  extractionsByDocument,
  markedTransactions,
}: {
  findings: FindingOut[];
  rules: RuleOut[];
  canReview: boolean;
  runId: string;
  linksByTransaction: Map<string, InvoiceLinkOut[]>;
  extractionsByDocument: Map<string, InvoiceExtractionOut>;
  markedTransactions: Set<string>;
}) {
  const ruleById = useMemo(
    () => new Map<string, RuleOut>(rules.map((r) => [r.id, r])),
    [rules],
  );

  // Own filter state — independent of the queue tab's filters so the
  // user can investigate the audit log without disturbing their review
  // position. Defaults: status="" (all) since pending rows aren't audit
  // events anyway; the table filters them out below.
  const [ruleFilter, setRuleFilter] = useState<string>('');
  const [provinceFilter, setProvinceFilter] = useState<string>('');
  const [supplierFilter, setSupplierFilter] = useState<string>('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const ruleIds = useMemo(
    () => Array.from(new Set(findings.map((f) => f.rule_id))).sort(),
    [findings],
  );
  const provinces = useMemo(() => {
    const s = new Set<string>();
    for (const f of findings) {
      const v = f.evidence['supplier.province'];
      if (typeof v === 'string' && v.length > 0) s.add(v);
    }
    return Array.from(s).sort();
  }, [findings]);
  const suppliers = useMemo(() => {
    const s = new Set<string>();
    for (const f of findings) {
      const v = f.evidence['supplier.name'];
      if (typeof v === 'string' && v.length > 0) s.add(v);
    }
    return Array.from(s).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
  }, [findings]);
  const transactionTypes = useMemo(() => {
    const s = new Set<string>();
    for (const f of findings) {
      const v = f.evidence['transaction_type'];
      if (typeof v === 'string' && v.length > 0) s.add(v);
    }
    return Array.from(s).sort();
  }, [findings]);

  const filtered = useMemo(() => {
    const q = deferredSearchQuery.trim().toLowerCase();
    const rows = findings.filter((f) => {
      if (ruleFilter && f.rule_id !== ruleFilter) return false;
      if (provinceFilter && f.evidence['supplier.province'] !== provinceFilter) return false;
      if (
        supplierFilter &&
        f.evidence['supplier.name'] !== supplierFilter
      )
        return false;
      if (
        transactionTypeFilter &&
        f.evidence['transaction_type'] !== transactionTypeFilter
      )
        return false;
      if (statusFilter && f.status !== statusFilter) return false;
      if (q && !findingMatchesSearch(f, q)) return false;
      // Audit log only shows dispositioned rows (reviewed_at != null).
      // Pending rows live on the Review queue tab; needs_review counts
      // as a disposition the practitioner explicitly made (with a note),
      // so it appears here alongside approved / rejected.
      return f.status !== 'pending';
    });
    // Most recent disposition first.
    rows.sort((a, b) => (b.reviewed_at ?? '').localeCompare(a.reviewed_at ?? ''));
    return rows;
  }, [
    findings,
    ruleFilter,
    provinceFilter,
    supplierFilter,
    transactionTypeFilter,
    statusFilter,
    deferredSearchQuery,
  ]);

  // On return from a finding detail page, scroll the row we came from back
  // into view and highlight it so the practitioner keeps their place.
  const visibleIds = useMemo(
    () => filtered.map((f) => f.finding_id),
    [filtered],
  );
  const { lastVisitedId, containerRef } = useReturnToRow(
    lastVisitedFindingKey(runId),
    visibleIds,
  );

  // Windowed rendering (INP-778): only the visible slice of the audit log
  // lands in the DOM — a full-population run's rejected wall is tens of
  // thousands of rows. Per-entry measurement means a wrapping note just
  // measures taller; no fixed-height contract. Inactive below ~200 rows.
  const { setContainer, rowWindow } = useWindowedRows(filtered.length);
  const attachTableContainer = (el: HTMLDivElement | null) => {
    containerRef.current = el;
    setContainer(el);
  };
  const renderedRows = filtered.slice(rowWindow.start, rowWindow.end);

  const hasActiveFilter = Boolean(
    ruleFilter ||
      provinceFilter ||
      supplierFilter ||
      transactionTypeFilter ||
      statusFilter ||
      searchQuery.trim(),
  );

  const clearAllFilters = () => {
    setRuleFilter('');
    setProvinceFilter('');
    setSupplierFilter('');
    setTransactionTypeFilter('');
    setStatusFilter('');
    setSearchQuery('');
  };

  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [bulkProgress, setBulkProgress] = useState<{
    action: 'approve' | 'reject' | 'unreview';
    done: number;
    total: number;
    errors: number;
  } | null>(null);
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkApproveNote, setBulkApproveNote] = useState('');
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  // Reason codes for the audit-log bulk-reject path. Parallel to the
  // review-queue state higher up — every reject surface must thread
  // codes through so the structured signal is uniform across the app.
  const [bulkRejectCodes, setBulkRejectCodes] = useState<ReasonCode[]>([]);

  // Drop selections whose row is no longer in the filtered view — keeps
  // the "N selected" counter honest when filters narrow.
  useEffect(() => {
    if (selected.size === 0) return;
    const visible = new Set(filtered.map((f) => f.finding_id));
    let changed = false;
    const pruned = new Set<string>();
    for (const id of selected) {
      if (visible.has(id)) pruned.add(id);
      else changed = true;
    }
    if (changed) setSelected(pruned);
  }, [filtered, selected]);

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((f) => selected.has(f.finding_id));
  const someVisibleSelected = !allVisibleSelected && filtered.some((f) => selected.has(f.finding_id));

  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((f) => f.finding_id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulkUnreview = async () => {
    if (!isSignedIn || selected.size === 0 || bulkInFlight) return;
    const ids = Array.from(selected);
    setBulkProgress({ action: 'unreview', done: 0, total: ids.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < ids.length; i++) {
      try {
        await unreviewFinding(ids[i], (await getIdToken()) ?? '');
      } catch (e) {
        errors += 1;
        console.error('bulk unreview failed for', ids[i], e);
      }
      setBulkProgress({ action: 'unreview', done: i + 1, total: ids.length, errors });
    }
    qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    setSelected(new Set());
    window.setTimeout(() => setBulkProgress(null), 1500);
  };

  const runBulkApproveSelected = async () => {
    // bulkInFlight guard: the Cmd/Ctrl+Enter submit bypasses the disabled
    // button, so a second keystroke could overlap an in-flight pass.
    if (!isSignedIn || selected.size === 0 || bulkInFlight) return;
    const note = bulkApproveNote.trim();
    if (note.length === 0) return;
    const ids = Array.from(selected);
    const byId = new Map(filtered.map((f) => [f.finding_id, f]));
    setBulkProgress({ action: 'approve', done: 0, total: ids.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < ids.length; i++) {
      try {
        await approveFinding(
          ids[i],
          (await getIdToken()) ?? '',
          bulkApprovalReason(byId.get(ids[i])?.approval_reason),
          note,
        );
      } catch (e) {
        errors += 1;
        console.error('bulk approve failed for', ids[i], e);
      }
      setBulkProgress({ action: 'approve', done: i + 1, total: ids.length, errors });
    }
    qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    setBulkApproveOpen(false);
    setBulkApproveNote('');
    setSelected(new Set());
    window.setTimeout(() => setBulkProgress(null), 1500);
  };

  const runBulkRejectSelected = async () => {
    if (!isSignedIn || selected.size === 0 || bulkInFlight) return;
    const reason = bulkRejectReason.trim();
    // Snapshot the codes once so a mid-loop edit can't poison the batch.
    const codes = bulkRejectCodes;
    // At least one signal required — codes OR text.
    if (reason.length === 0 && codes.length === 0) return;
    const ids = Array.from(selected);
    setBulkProgress({ action: 'reject', done: 0, total: ids.length, errors: 0 });
    let errors = 0;
    for (let i = 0; i < ids.length; i++) {
      try {
        await rejectFinding(ids[i], reason, (await getIdToken()) ?? '', codes);
      } catch (e) {
        errors += 1;
        console.error('bulk reject failed for', ids[i], e);
      }
      setBulkProgress({ action: 'reject', done: i + 1, total: ids.length, errors });
    }
    qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    setBulkRejectOpen(false);
    setBulkRejectReason('');
    setBulkRejectCodes([]);
    setSelected(new Set());
    window.setTimeout(() => setBulkProgress(null), 1500);
  };

  const bulkInFlight = bulkProgress !== null && bulkProgress.done < bulkProgress.total;

  return (
    <div className="grid gap-4">
      <FindingFilterBar
        ruleFilter={ruleFilter}
        setRuleFilter={setRuleFilter}
        provinceFilter={provinceFilter}
        setProvinceFilter={setProvinceFilter}
        supplierFilter={supplierFilter}
        setSupplierFilter={setSupplierFilter}
        transactionTypeFilter={transactionTypeFilter}
        setTransactionTypeFilter={setTransactionTypeFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        ruleIds={ruleIds}
        ruleById={ruleById}
        provinces={provinces}
        suppliers={suppliers}
        transactionTypes={transactionTypes}
        totalCount={findings.length}
        showingCount={filtered.length}
        hasActiveFilter={hasActiveFilter}
        onClearAll={clearAllFilters}
      />

      {filtered.length === 0 ? (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>No reviews to display</CardTitle>
            <CardDescription>
              {findings.some((f) => f.status !== 'pending')
                ? 'No reviews match the active filters. Clear them above to see all dispositions.'
                : 'No findings on this run have been approved or rejected yet. Switch to the Review queue tab to disposition findings.'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          {canReview && (selected.size > 0 || bulkProgress !== null) && (
            <div className="flex items-center justify-between gap-3 rounded-md border border-sky-500/40 bg-sky-50 px-3 py-2 text-sm text-sky-900">
              <span>
                {bulkProgress
                  ? `${
                      bulkProgress.action === 'approve'
                        ? 'Approving'
                        : bulkProgress.action === 'reject'
                          ? 'Rejecting'
                          : 'Unreviewing'
                    } ${bulkProgress.done} / ${bulkProgress.total}${bulkProgress.errors > 0 ? ` (${bulkProgress.errors} failed)` : ''}`
                  : `${selected.size} selected`}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={bulkInFlight || selected.size === 0}
                  onClick={() => setSelected(new Set())}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-sky-500/60 bg-white text-sky-800 hover:bg-sky-100"
                  disabled={bulkInFlight || selected.size === 0}
                  onClick={() => setBulkApproveOpen(true)}
                  title="Approve all selected findings with a note (applies to every selected row, including ones already approved)"
                >
                  Approve {selected.size} selected
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-rose-500/60 bg-white text-rose-700 hover:bg-rose-50"
                  disabled={bulkInFlight || selected.size === 0}
                  onClick={() => setBulkRejectOpen(true)}
                  title="Reject all selected findings with a reason (applies to every selected row, including ones already rejected)"
                >
                  Reject {selected.size} selected
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-sky-500/60 bg-white text-sky-800 hover:bg-sky-100"
                  disabled={bulkInFlight || selected.size === 0}
                  onClick={runBulkUnreview}
                  title="Send all selected findings back to the review queue"
                >
                  {bulkInFlight && bulkProgress?.action === 'unreview'
                    ? 'Unreviewing…'
                    : `Unreview ${selected.size} selected`}
                </Button>
              </div>
            </div>
          )}
          <div
            ref={attachTableContainer}
            className="overflow-x-auto rounded-md border border-border"
          >
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-brand-slate">
                <tr>
                  {canReview && (
                    <th className="w-9 px-3 py-2 text-left font-medium">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        checked={allVisibleSelected}
                        disabled={bulkInFlight}
                        onChange={toggleAllVisible}
                        className="h-4 w-4 cursor-pointer rounded border-input accent-sky-600 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Select all visible findings"
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 text-left font-medium">When</th>
                  <th className="px-3 py-2 text-left font-medium">By</th>
                  <th className="px-3 py-2 text-left font-medium">Action</th>
                  <th className="px-3 py-2 text-left font-medium">Rule</th>
                  <th className="px-3 py-2 text-left font-medium">Tags</th>
                  <th className="px-3 py-2 text-left font-medium">Supplier</th>
                  <th className="px-3 py-2 text-left font-medium">Tx</th>
                  <th className="px-3 py-2 text-right font-medium">Recovery</th>
                  <th className="px-3 py-2 text-left font-medium">Note / reason</th>
                  {canReview && <th className="px-3 py-2 text-right font-medium">Override</th>}
                </tr>
              </thead>
              <tbody>
                {rowWindow.topPad > 0 && (
                  <tr data-window-spacer aria-hidden style={{ height: rowWindow.topPad }} />
                )}
                {renderedRows.map((f, k) => (
                  <AuditRow
                    key={f.finding_id}
                    windowIndex={rowWindow.start + k}
                    finding={f}
                    rule={ruleById.get(f.rule_id) ?? null}
                    canReview={canReview}
                    runId={runId}
                    selected={selected.has(f.finding_id)}
                    onToggleSelected={() => toggleOne(f.finding_id)}
                    disabled={bulkInFlight}
                    invoiceLinks={linksByTransaction.get(f.transaction_id)}
                    invoiceExtractions={extractionsByDocument}
                    isMarkedNoInvoice={markedTransactions.has(f.transaction_id)}
                    isLastVisited={f.finding_id === lastVisitedId}
                  />
                ))}
                {rowWindow.bottomPad > 0 && (
                  <tr
                    data-window-spacer
                    aria-hidden
                    style={{ height: rowWindow.bottomPad }}
                  />
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog
        open={bulkApproveOpen}
        onClose={() => {
          setBulkApproveOpen(false);
          setBulkApproveNote('');
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk approve {selected.size} findings?</DialogTitle>
          <DialogDescription>
            Approves every selected finding. The same note is recorded on
            each; the audit trail keeps individual reviewer + timestamp
            per finding. Findings already approved are re-approved with
            the new note; rejected findings flip to approved.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (bulkApproveNote.trim().length === 0) return;
            void runBulkApproveSelected();
          }}
        >
          <textarea
            value={bulkApproveNote}
            onChange={(e) => setBulkApproveNote(e.target.value)}
            onKeyDown={submitFormOnCmdEnter}
            placeholder="Why are these approvable? (required, applies to all)"
            maxLength={256}
            required
            className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <DialogFooter>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBulkApproveOpen(false);
                setBulkApproveNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={bulkApproveNote.trim().length === 0 || bulkInFlight}
            >
              Approve all {selected.size}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

      <Dialog
        open={bulkRejectOpen}
        onClose={() => {
          setBulkRejectOpen(false);
          setBulkRejectReason('');
          setBulkRejectCodes([]);
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk reject {selected.size} findings?</DialogTitle>
          <DialogDescription>
            Rejects every selected finding. The same reason + codes are
            recorded on each; the audit trail keeps individual reviewer +
            timestamp per finding. Findings already rejected are
            re-rejected with the new reason; approved findings flip.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            // At least one signal required — codes OR text.
            if (bulkRejectReason.trim().length === 0 && bulkRejectCodes.length === 0) return;
            void runBulkRejectSelected();
          }}
        >
          <ReasonCodesPicker value={bulkRejectCodes} onChange={setBulkRejectCodes} />
          <textarea
            value={bulkRejectReason}
            onChange={(e) => setBulkRejectReason(e.target.value)}
            onKeyDown={submitFormOnCmdEnter}
            placeholder="Optional when codes are picked — otherwise required (applies to all)"
            maxLength={256}
            className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <DialogFooter>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBulkRejectOpen(false);
                setBulkRejectReason('');
                setBulkRejectCodes([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              variant="destructive"
              disabled={
                (bulkRejectReason.trim().length === 0 && bulkRejectCodes.length === 0) ||
                bulkInFlight
              }
            >
              Reject all {selected.size}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
    </div>
  );
}


function AuditRow({
  finding,
  windowIndex,
  rule,
  canReview,
  runId,
  selected,
  onToggleSelected,
  disabled,
  invoiceLinks,
  invoiceExtractions,
  isMarkedNoInvoice,
  isLastVisited,
}: {
  finding: FindingOut;
  /** Index in the filtered row list — the windowing measurement key. */
  windowIndex: number;
  rule: RuleOut | null;
  canReview: boolean;
  runId: string;
  selected: boolean;
  onToggleSelected: () => void;
  disabled: boolean;
  invoiceLinks?: InvoiceLinkOut[];
  invoiceExtractions?: Map<string, InvoiceExtractionOut>;
  isMarkedNoInvoice?: boolean;
  isLastVisited?: boolean;
}) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();

  const unreview = useMutation({
    mutationFn: async () => unreviewFinding(finding.finding_id, (await getIdToken() ?? '')),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
      qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    },
  });
  const supplier = stringOrNull(finding.evidence['supplier.name']) ?? '—';
  const note =
    finding.approval_note ?? finding.rejection_reason ?? finding.review_note ?? null;
  const reviewedAt = finding.reviewed_at
    ? formatDateTime(finding.reviewed_at)
    : '—';

  const rowTint =
    finding.status === 'approved'
      ? 'bg-emerald-50/40'
      : finding.status === 'rejected'
        ? 'bg-destructive/5'
        : finding.status === 'needs_review'
          ? 'bg-amber-50/40'
          : '';
  // The row last opened from this table gets a blue highlight (over the
  // status tint) so the practitioner can see where they navigated from.
  // Mirrors the list view's returning-row highlight.
  const highlight = isLastVisited
    ? 'bg-brand-royal/10 ring-2 ring-inset ring-brand-royal/40'
    : rowTint;

  const rowDisabled = disabled || unreview.isPending;

  return (
    <>
      <tr
        data-row-id={finding.finding_id}
        data-window-index={windowIndex}
        className={`relative cursor-pointer border-t border-border transition-colors hover:bg-brand-royal/5 ${highlight}`}
      >
        {canReview && (
          <td
            className="relative z-10 whitespace-nowrap px-3 py-2"
            onClick={stopRowActivation}
          >
            <input
              type="checkbox"
              checked={selected}
              disabled={rowDisabled}
              onChange={onToggleSelected}
              className="h-4 w-4 cursor-pointer rounded border-input accent-sky-600 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={`Select ${rule?.aa_code ?? finding.rule_id} finding on ${supplier}`}
            />
          </td>
        )}
        <td className="whitespace-nowrap px-3 py-2 text-xs text-brand-slate">
          {reviewedAt}
        </td>
        <td className="whitespace-nowrap px-3 py-2">
          {finding.reviewer_alias ?? <span className="text-brand-slate">—</span>}
        </td>
        <td className="whitespace-nowrap px-3 py-2">
          <FindingStatusBadge kind="finding" status={finding.status} />
          {finding.status === 'approved' && finding.approval_reason && (
            <FindingStatusBadge
              kind="approval"
              status={finding.approval_reason}
              className="ml-1.5"
            />
          )}
          <CarriedForwardPill finding={finding} />
        </td>
        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-brand-navy">
          <Link
            to="/runs/$id/findings/$findingId"
            params={{ id: runId, findingId: finding.finding_id }}
            onClick={() =>
              writeLastVisitedRow(lastVisitedFindingKey(runId), finding.finding_id)
            }
            // Stretched link: the ::after overlay makes the whole row a real
            // anchor — left-click navigates (client-side), right / cmd /
            // middle-click open the finding in a new tab. Interactive cells
            // sit above it via `relative z-10`.
            className="underline-offset-4 after:absolute after:inset-0 after:content-[''] hover:underline"
            aria-label={`Open detail for ${rule?.aa_code ?? finding.rule_id} finding on ${supplier}`}
          >
            {rule?.aa_code ?? finding.rule_id}
          </Link>
        </td>
        <td className="whitespace-nowrap px-3 py-2">
          <FindingTags finding={finding} />
        </td>
        <td className="px-3 py-2 text-brand-navy">{supplier}</td>
        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-brand-slate">
          {finding.transaction_id}
          {invoiceLinks && invoiceLinks.length > 0 && (
            <span className="relative z-10 ml-1.5" onClick={stopRowActivation}>
              <AttachmentChip
                links={invoiceLinks}
                extractions={invoiceExtractions}
                customerSlug={null}
                compact
              />
            </span>
          )}
          {isMarkedNoInvoice && (!invoiceLinks || invoiceLinks.length === 0) && (
            <NoInvoiceBadge />
          )}
        </td>
        <td
          className="relative z-10 whitespace-nowrap px-3 py-2 text-right"
          onClick={stopRowActivation}
        >
          <RecoveryAmountInline
            finding={finding}
            canReview={canReview}
            runId={runId}
            textClassName="font-mono tabular-nums text-brand-navy"
          />
        </td>
        <td className="px-3 py-2 text-brand-slate">
          {note ?? <span className="text-brand-slate/60">—</span>}
        </td>
        {canReview && (
          <td
            className="relative z-10 whitespace-nowrap px-3 py-2 text-right"
            onClick={stopRowActivation}
          >
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 border-sky-500/40 text-sky-800 hover:bg-sky-50"
              disabled={rowDisabled}
              onClick={() => unreview.mutate()}
              title="Send this finding back to the review queue (clears reviewer + note)"
            >
              {unreview.isPending ? '…' : 'Unreview'}
            </Button>
          </td>
        )}
      </tr>
    </>
  );
}

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.length > 0 ? value : null;
}
